import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Spinner } from "@/components/Common";
import { api, errMsg } from "@/lib/api";
import { STAGES } from "@/lib/rcc";
import { Badge, Cell, Pending, Score, Value, pretty } from "@/components/rcc/Cells";
import { Md } from "@/components/admin/Md";
import { cn } from "@/lib/utils";

const useGet = (url) => {
  const [d, setD] = useState(null);
  const [missing, setMissing] = useState(false);
  useEffect(() => { api.get(url).then((r) => setD(r.data || {})).catch((e) => { if (e.response?.status === 404) setMissing(true); else toast.error(errMsg(e)); setD({}); }); }, [url]);
  return [d, missing, setD];
};

const Kpi = ({ k, v, sub }) => (
  <div className="rounded-xl border border-[#27272A] bg-[#121214] p-3" data-testid={`rcc-kpi-${k}`}>
    <p className="text-[10px] uppercase tracking-wider text-zinc-500">{pretty(k)}</p>
    <p className="mt-1 font-display text-2xl text-zinc-50">{v == null ? "—" : typeof v === "number" && k.includes("revenue") ? `$${v.toLocaleString()}` : String(v)}</p>
    {sub && <p className="text-[10px] text-zinc-500">{sub}</p>}
  </div>
);

const KPI_KEYS = ["companies_discovered", "qualified_companies", "decision_makers", "verified_contacts", "active_mpcs", "active_campaigns", "emails_delivered", "replies", "positive_reply_rate", "meetings", "proposals", "closed_deals", "placements", "revenue", "forecast_revenue", "pipeline_value"];

const DashItem = ({ o, reply }) => {
  if (typeof o !== "object" || !o) return <li className="text-sm text-zinc-200">{String(o)}</li>;
  const main = reply ? o.contact_name || o.company_name : o.name || o.company_name;
  const sub = reply ? [o.company_name !== main && o.company_name, o.subject].filter(Boolean).join(" · ") : o.reason || o.signal || o.industry;
  return (
    <li><Link to={reply ? "/admin/rcc/inbox" : "/admin/rcc/companies"} className="flex items-center justify-between gap-2 rounded-md px-1 py-0.5 hover:bg-[#8224e3]/10">
      <span className="min-w-0"><span className="block truncate text-sm text-zinc-100">{main || "—"}</span>{sub && <span className="block truncate text-[11px] text-zinc-500">{sub}</span>}</span>
      {reply ? o.label && <Badge v={o.label} /> : o.opportunity_score != null && <Score v={o.opportunity_score} />}
    </Link></li>
  );
};

export const RccDashboard = () => {
  const [d, missing] = useGet("/admin/rcc/dashboard");
  if (!d) return <Spinner className="h-6 w-6 text-[#8224e3]" />;
  return (
    <div className="space-y-5" data-testid="rcc-dashboard">
      {missing && <Pending what="Needs GET /admin/rcc/dashboard (KPIs, priorities, hot companies, replies needing attention)." />}
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4 xl:grid-cols-8">{KPI_KEYS.map((k) => <Kpi key={k} k={k} v={d.kpis?.[k]} />)}</div>
      <div className="grid gap-4 lg:grid-cols-3">
        {[["priorities", "Today's priorities"], ["hot_companies", "Hot companies"], ["needs_reply", "Replies needing attention"]].map(([k, l]) => (
          <div key={k} className="rounded-xl border border-[#27272A] bg-[#121214] p-4" data-testid={`rcc-dash-${k}`}>
            <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-[#d7b6ff]">{l}</p>
            {d[k]?.length ? (k === "priorities" ? <Value v={d[k]} /> : <ul className="space-y-1.5">{d[k].map((o, i) => <DashItem key={o?.id || i} o={o} reply={k === "needs_reply"} />)}</ul>) : <p className="text-xs text-zinc-500">Nothing yet.</p>}
          </div>
        ))}
      </div>
      {d.briefing && <div className="rounded-xl border border-[#8224e3]/30 bg-[#8224e3]/5 p-4" data-testid="rcc-dash-briefing"><p className="mb-2 text-xs font-semibold uppercase tracking-wider text-[#d7b6ff]">Daily sales briefing</p><Md text={d.briefing} /></div>}
    </div>
  );
};

export const RccPipeline = () => {
  const [d, missing, setD] = useGet("/admin/rcc/opportunities");
  const items = d?.items || [];
  const move = async (o, stage) => {
    setD((x) => ({ ...x, items: x.items.map((i) => (i.id === o.id ? { ...i, stage } : i)) }));
    try { await api.patch(`/admin/rcc/opportunities/${o.id}`, { stage }); } catch (e) { toast.error(errMsg(e)); }
  };
  if (!d) return <Spinner className="h-6 w-6 text-[#8224e3]" />;
  const total = (s) => items.filter((i) => i.stage === s).reduce((n, i) => n + (i.value || 0), 0);
  return (
    <div className="space-y-3" data-testid="rcc-pipeline">
      {missing && <Pending what="Needs GET/PATCH /admin/rcc/opportunities." />}
      <div className="flex gap-3 overflow-x-auto pb-3">
        {STAGES.map((s) => (
          <div key={s} className="w-64 shrink-0 rounded-xl border border-[#27272A] bg-[#0f0f12] p-2" data-testid={`rcc-col-${s}`}>
            <div className="mb-2 flex items-center justify-between px-1"><Badge v={s} /><span className="font-mono text-[10px] text-zinc-500">${total(s).toLocaleString()}</span></div>
            <div className="space-y-2">{items.filter((i) => i.stage === s).map((o) => (
              <div key={o.id} className="rounded-lg border border-[#27272A] bg-[#18181B] p-2.5" data-testid={`rcc-opp-${o.id}`}>
                <p className="text-sm text-zinc-100">{o.name}</p>
                <p className="text-[11px] text-zinc-500">{o.company_name}{o.service ? ` · ${pretty(o.service)}` : ""}</p>
                <div className="mt-1.5 flex items-center justify-between"><Cell v={o.value} kind="money" />
                  <select value={o.stage} onChange={(e) => move(o, e.target.value)} className="h-7 max-w-[120px] rounded border border-[#27272A] bg-[#0b0b0e] text-[10px] capitalize text-zinc-300" data-testid={`rcc-opp-stage-${o.id}`}>{STAGES.map((x) => <option key={x} value={x}>{pretty(x)}</option>)}</select></div>
              </div>
            ))}</div>
          </div>
        ))}
      </div>
    </div>
  );
};

const EXAMPLES = ["Find 25 companies hiring Solidity developers that recently raised funding", "Which MPC has the most matching companies?", "Show prospects who replied positively but haven't had a response", "Prepare the 20 highest-value prospects for this week", "Build a campaign for our senior Rust engineer"];

export const RccStrategist = () => {
  const [msgs, setMsgs] = useState([]);
  const [sid, setSid] = useState(null);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const send = async (q = text) => {
    if (!q.trim()) return;
    setMsgs((m) => [...m, { role: "user", content: q }]); setText(""); setBusy(true);
    try { const { data } = await api.post("/admin/rcc/strategist", { session_id: sid, message: q }); setSid(data.session_id); setMsgs((m) => [...m, { role: "assistant", content: data.reply, staged: data.staged_actions }]); }
    catch (e) { setMsgs((m) => [...m, { role: "assistant", content: e.response?.status === 404 ? "The AI Sales Strategist isn't live on the backend yet (spec §31, POST /admin/rcc/strategist)." : errMsg(e) }]); }
    finally { setBusy(false); }
  };
  return (
    <div className="space-y-3" data-testid="rcc-strategist">
      <div className="flex flex-wrap gap-1.5">{EXAMPLES.map((e, i) => <button key={e} onClick={() => send(e)} className="rounded-full border border-[#8224e3]/40 px-3 py-1 text-xs text-[#d7b6ff] hover:bg-[#8224e3]/15" data-testid={`rcc-strat-example-${i}`}>{e}</button>)}</div>
      <div className="min-h-[200px] space-y-2 rounded-xl border border-[#27272A] bg-[#0f0f12] p-3">
        {!msgs.length && <p className="text-sm text-zinc-500">Ask about prospects, MPCs, campaigns or revenue. The strategist prepares lists and drafts; you approve before anything is sent.</p>}
        {msgs.map((m, i) => (
          <div key={i} className={cn("max-w-[90%] rounded-xl px-3 py-2 text-sm", m.role === "user" ? "ml-auto bg-[#8224e3]/25 text-zinc-100" : "border border-[#27272A] bg-[#18181B] text-zinc-200")} data-testid={`rcc-strat-msg-${i}`}>
            {m.role === "user" ? m.content : <Md text={m.content} />}
            {m.staged?.length > 0 && <div className="mt-2 rounded-lg border border-amber-500/30 bg-amber-500/10 p-2 text-xs text-amber-200">Staged for your approval: <Value v={m.staged} /></div>}
          </div>
        ))}
        {busy && <Loader2 className="h-4 w-4 animate-spin text-[#8224e3]" />}
      </div>
      <div className="flex gap-2"><Textarea rows={2} value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && (e.preventDefault(), send())} placeholder="Ask the AI Sales Strategist…" className="bg-[#09090B]" data-testid="rcc-strat-input" />
        <Button onClick={() => send()} disabled={busy || !text.trim()} className="h-auto bg-[#8224e3] text-white hover:bg-[#9440f0]" data-testid="rcc-strat-send"><Send className="h-4 w-4" /></Button></div>
    </div>
  );
};

const FUNNEL = ["discovery", "qualification", "contact", "engagement", "meeting", "proposal", "job_order", "placement", "revenue"];

export const RccAnalytics = () => {
  const [period, setPeriod] = useState("30d");
  const [d, missing] = useGet(`/admin/rcc/analytics?period=${period}`);
  return (
    <div className="space-y-4" data-testid="rcc-analytics">
      <div className="flex gap-1">{["7d", "30d", "90d", "365d"].map((p) => <button key={p} onClick={() => setPeriod(p)} className={cn("rounded-full px-3 py-1 text-xs", p === period ? "bg-[#8224e3] text-white" : "border border-[#27272A] text-zinc-400")} data-testid={`rcc-period-${p}`}>{p}</button>)}</div>
      {missing && <Pending what="Needs GET /admin/rcc/analytics?period= (funnel, by_campaign, by_channel, by_recruiter, by_signal, by_mpc, revenue_actual, revenue_forecast)." />}
      {!d ? <Spinner className="h-6 w-6 text-[#8224e3]" /> : <>
        <div className="grid grid-cols-3 gap-2 sm:grid-cols-9">{FUNNEL.map((k) => <Kpi key={k} k={k} v={d.funnel?.[k]} />)}</div>
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-3"><Kpi k="revenue_actual" v={d.revenue_actual} sub="Closed and invoiced" /><Kpi k="revenue_forecast" v={d.revenue_forecast} sub="Weighted pipeline" /><Kpi k="bounce_rate" v={d.bounce_rate} /></div>
        <div className="grid gap-3 lg:grid-cols-2">{["by_campaign", "by_channel", "by_recruiter", "by_signal", "by_mpc", "by_industry"].map((k) => <div key={k} className="rounded-xl border border-[#27272A] bg-[#121214] p-3"><p className="mb-2 text-xs font-semibold uppercase tracking-wider text-[#d7b6ff]">{pretty(k)}</p>{d[k] ? <Value v={d[k]} /> : <p className="text-xs text-zinc-500">No data yet.</p>}</div>)}</div>
        {d.export_url && <a href={d.export_url} className="text-xs text-[#c79bff] hover:underline" data-testid="rcc-export">Export report</a>}
      </>}
    </div>
  );
};

export const ReuseLinks = ({ links }) => (
  <div className="flex flex-wrap gap-2" data-testid="rcc-reuse">{links.map(([to, l]) => <Link key={to} to={to} className="rounded-full border border-[#27272A] px-3 py-1 text-xs text-zinc-300 hover:border-[#8224e3]/60 hover:text-[#d7b6ff]">{l} →</Link>)}</div>
);
